import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Phone, CheckCircle2 } from "lucide-react";
import { Tile, Card, HBar, Loading, fmtN, timeAgo } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Overview({ headers, range, refreshKey, toast }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      setErr(false);
      const r = await axios.get(`${API}/admin/dashboard/overview?range=${range}`, headers());
      setD(r.data);
    } catch (e) {
      setErr(true);
    }
  }, [range, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const markContacted = async (id) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status: "Contacted" }, headers());
      toast({ title: "Marked contacted" });
      load();
    } catch (e) {
      toast({ title: "Couldn't update lead" });
    }
  };

  if (err) return <div className="mt-6 p-8 bg-[#0D0D0F] border border-white/10 text-neutral-400 text-sm">Couldn't load the overview — hit Refresh.</div>;
  if (!d) return <Loading />;

  const k = d.kpis;
  const maxCh = Math.max(1, ...d.channels.map((c) => c.leads));
  const maxInt = Math.max(1, ...d.interests.map((c) => c.leads));
  const maxDay = Math.max(1, ...d.daily.map((c) => c.leads));
  const speedAlert = k.median_speed_minutes != null && k.median_speed_minutes > k.speed_to_lead_target;

  return (
    <div className="mt-6 space-y-5" data-testid="overview-tab">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
        <Tile testid="kpi-leads" label="Leads" value={fmtN(k.leads)} sub={`${k.booked} booked · ${k.contacts} contact msgs`} />
        <Tile testid="kpi-contacted" label="Contacted %" value={`${k.contacted_pct}%`} sub="leads beyond New" />
        <Tile
          testid="kpi-speed"
          label="Median Speed-to-Lead"
          value={k.median_speed_minutes == null ? "—" : `${fmtN(k.median_speed_minutes)}m`}
          sub={`target ${k.speed_to_lead_target} min`}
          alert={speedAlert}
        />
        <Tile testid="kpi-notcontacted" label="Not Contacted >2h" value={fmtN(k.not_contacted_2h)} sub="still New" alert={k.not_contacted_2h > 0} />
        <Tile testid="kpi-booked" label="Goal Reviews Booked" value={fmtN(k.goal_reviews_booked)} />
        <Tile testid="kpi-joined" label="Joined" value={fmtN(k.joined)} />
        <Tile testid="kpi-pipeline" label="Pipeline Value" value={`$${fmtN(k.pipeline_value)}`} sub={`${k.joined} joined × $${fmtN(k.avg_membership_value)}`} />
        <Tile testid="kpi-mailchimp" label="Mailchimp Synced" value={`${k.mailchimp_pct}%`} sub="of leads in range" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Channels */}
        <Card title="Where Leads Come From" testid="card-channels">
          <div className="space-y-3.5">
            {d.channels.map((c) => (
              <div key={c.channel} data-testid={`channel-${c.channel.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <span className="text-[13px] text-neutral-200 font-medium">{c.channel}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-neutral-500 whitespace-nowrap">
                    {c.leads} leads · {c.booked} booked · {c.joined} joined
                  </span>
                </div>
                <HBar pct={(c.leads / maxCh) * 100} color={c.channel === "Exit Popup" ? "#E8C766" : "#9D0423"} />
              </div>
            ))}
          </div>
          <p className="mt-4 text-[10px] text-neutral-600">First-touch attribution from UTMs, gclid, referrer and form. Exit Popup leads also count in their source channel.</p>
        </Card>

        {/* Interests */}
        <div className="space-y-5">
          <Card title="By Interest" testid="card-interests">
            {d.interests.length === 0 && <p className="text-[13px] text-neutral-500">No leads in this range yet.</p>}
            <div className="space-y-3.5">
              {d.interests.map((c) => (
                <div key={c.interest}>
                  <div className="flex items-baseline justify-between gap-3 mb-1.5">
                    <span className="text-[13px] text-neutral-200 font-medium">{c.interest}</span>
                    <span className="font-mono text-[10px] text-neutral-500">{c.leads}</span>
                  </div>
                  <HBar pct={(c.leads / maxInt) * 100} />
                </div>
              ))}
            </div>
          </Card>

          <Card title="Leads Over Time" testid="card-leads-chart">
            <div className="flex items-end gap-px h-28" aria-label="Daily leads bar chart">
              {d.daily.map((day) => (
                <div
                  key={day.date}
                  title={`${day.date} · ${day.leads} lead${day.leads === 1 ? "" : "s"}`}
                  className="flex-1 bg-[#9D0423]/80 hover:bg-[#E56A79] transition-colors min-w-[2px]"
                  style={{ height: `${day.leads ? Math.max(8, (day.leads / maxDay) * 100) : 2}%`, opacity: day.leads ? 1 : 0.25 }}
                />
              ))}
            </div>
            <div className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-[0.1em] text-neutral-600">
              <span>{d.daily[0]?.date}</span>
              <span>{d.daily[d.daily.length - 1]?.date}</span>
            </div>
          </Card>
        </div>
      </div>

      {/* Follow-up queue */}
      <Card title="Follow-Up Queue" right={<span className="font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-500">10 oldest still New</span>} testid="card-followup">
        {d.followup_queue.length === 0 ? (
          <p className="flex items-center gap-2 text-[13px] text-emerald-400" data-testid="followup-empty">
            <CheckCircle2 size={15} /> Inbox zero — every lead in this range has been contacted.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  {["Name", "Interest", "Phone", "Prefers", "Best Time", "Age", ""].map((c, i) => (
                    <th key={c || i} className="pb-3 pr-4 font-mono text-[9px] uppercase tracking-[0.16em] text-neutral-500 whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.followup_queue.map((l) => (
                  <tr key={l.id} className="border-t border-white/5" data-testid={`followup-${l.id}`}>
                    <td className="py-3 pr-4 text-white font-semibold whitespace-nowrap">{l.name}</td>
                    <td className="py-3 pr-4 text-neutral-300 text-[12px]">{l.program_interest || "—"}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {l.phone ? (
                        <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 text-[#E56A79] hover:text-white transition-colors">
                          <Phone size={12} /> {l.phone}
                        </a>
                      ) : (
                        <span className="text-neutral-600">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-neutral-400 text-[12px]">{l.preferred_contact_method || "—"}</td>
                    <td className="py-3 pr-4 text-neutral-400 text-[12px] whitespace-nowrap">{[l.preferred_day, l.preferred_time].filter(Boolean).join(" · ") || "—"}</td>
                    <td className={`py-3 pr-4 font-mono text-[11px] whitespace-nowrap ${l.age_minutes > 120 ? "text-[#E56A79]" : "text-neutral-400"}`}>{timeAgo(l.created_at)}</td>
                    <td className="py-3">
                      <button
                        onClick={() => markContacted(l.id)}
                        data-testid={`mark-contacted-${l.id}`}
                        className="font-mono text-[10px] uppercase tracking-[0.12em] text-white bg-[#9D0423] hover:bg-[#7E031C] px-3 py-1.5 transition-colors whitespace-nowrap"
                      >
                        Mark contacted
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
